// Spares Audit — the maintenance manager vets spare purchases (PI) and spare
// issues (SPI) and marks each voucher audited, individually or in a logged bulk
// batch, or raises a query back to the store.
// Plan and data rules: docs/spares-audit-plan.md. Server contract:
// docs/BACKEND-REQUEST-spares-audit.md.
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { connect } from 'react-redux'
import { Button, DatePicker, Input, Select, Spin, message } from 'antd'
import moment from 'moment'
import PhotoLightbox from '../ReconciliationV3/PhotoLightbox'
import QueueList from './QueueList'
import FocusPanel from './FocusPanel'
import BulkDialog from './BulkDialog'
import AuditLog from './AuditLog'
import RaiseQueryDialog from './RaiseQueryDialog'
import {
  USE_MOCK, getQueue, getVoucher, auditVoucher, raiseQuery, reopenAudit, bulkAudit, getAssignableUsers,
  bulkAuditByFilter, getBatch, exportQueue, errorText, errorCode
} from './api'
import {
  STATUS_META, statusOf, allChecks, checkLabel, checkOrder, setCheckTypes, bulkEligible, suggestField, flagKey, recordAccess,
  inr, isImage, can, newRequestId
} from './helpers'
import './style.css'

const PAGE = 50
const BULK_MAX = 200
const BATCH_POLL_MS = 5000

const TILES = [
  { key: 'OPEN', label: 'Open', cls: '' },
  { key: 'NEW', label: STATUS_META.NEW.tile, cls: 'new' },
  { key: 'FLAGGED', label: 'Has checks', cls: 'flag' },
  { key: 'QUERY', label: STATUS_META.QUERY.tile, cls: 'query' },
  { key: 'CHANGED', label: STATUS_META.CHANGED.tile, cls: 'changed' },
  { key: 'AUDITED', label: STATUS_META.AUDITED.tile, cls: 'audited' }
]

// Does a row still belong in the current view after its status changed?
const matchesView = (item, status, check) => {
  const s = statusOf(item)
  const n = allChecks(item).length
  const okStatus = status === 'ALL' ? true
    : status === 'OPEN' ? s !== 'AUDITED'
      : status === 'FLAGGED' ? (s !== 'AUDITED' && n > 0)
        : s === status
  if (!okStatus) return false
  if (!check) return true
  if (check === 'NONE') return n === 0
  return allChecks(item).some(c => c.code === check)
}

export const SparesAudit = ({ authUser }) => {
  // Links into other modules follow the user's role there (edit / view / plain text).
  const links = useMemo(() => ({ pi: recordAccess(authUser, 'PurchaseOrder'), spare: recordAccess(authUser, 'SparePart'), jobCard: recordAccess(authUser, 'JobCard') }), [authUser])
  // Role permissions only, also when the dev mock is on.
  const perms = useMemo(() => ({ any: can(authUser, 'any'), r: can(authUser, 'r'), c: can(authUser, 'c'), bulk: can(authUser, 'bulk'), reopen: can(authUser, 'reopen') }), [authUser])

  // ---- filters ----
  const [type, setType] = useState('ALL')
  const [range, setRange] = useState([])
  const [location, setLocation] = useState(undefined)
  const [searchInput, setSearchInput] = useState('')
  const [search, setSearch] = useState('')
  const [status, setStatus] = useState('OPEN')
  const [check, setCheck] = useState(null)
  const [sort, setSort] = useState('newest')

  useEffect(() => { const t = setTimeout(() => setSearch(searchInput.trim()), 300); return () => clearTimeout(t) }, [searchInput])

  const locations = useMemo(() => ((authUser && authUser.locations) || []).filter(l => l && l._id).slice().sort((a, b) => String(a.name).localeCompare(String(b.name))), [authUser])

  const filters = useMemo(() => ({
    type,
    // Exact instants in the browser's timezone, so the server never guesses one.
    from: range && range[0] ? range[0].clone().startOf('day').toISOString() : null,
    to: range && range[1] ? range[1].clone().endOf('day').toISOString() : null,
    location: location || null,
    search: search || null,
    status,
    check,
    sort
  }), [type, range, location, search, status, check, sort])
  const filterKey = JSON.stringify(filters)

  // ---- queue ----
  const [q, setQ] = useState({ items: [], count: 0, counts: null, checkCounts: {}, eligibleInView: 0, valueOpen: null, valueOpenExcludedLines: 0, settings: null, dataAsOf: null })
  const [loading, setLoading] = useState(false)
  const [loadingMore, setLoadingMore] = useState(false)
  const [error, setError] = useState(null)
  const [curId, setCurId] = useState(null)
  const [sel, setSel] = useState({})
  const [running, setRunning] = useState([]) // [{batchId, voucherCount}]
  const seq = useRef(0)

  // Deep link from an Audit Query alert: /spares-audit?voucher=PI:<id> (or SPI:<id>).
  // The linked voucher stays pinned at the top of the queue until another voucher is picked,
  // even when it falls outside the current filters.
  const pinned = useRef(null)
  const deepLink = useRef((() => {
    try {
      const m = /^(PI|SPI):([0-9a-fA-F]{24})$/.exec(new URLSearchParams(window.location.search).get('voucher') || '')
      return m ? { type: m[1], id: m[2] } : null
    } catch (e) { return null }
  })())
  const withPinned = (list) => (pinned.current && !list.some(x => x.voucherId === pinned.current.voucherId) ? [pinned.current].concat(list) : list)

  const load = useCallback((offset) => {
    const my = ++seq.current
    if (offset) setLoadingMore(true); else setLoading(true)
    setError(null)
    return getQueue({ ...filters, limit: PAGE, offset })
      .then(res => {
        if (my !== seq.current) return
        setCheckTypes(res.checkTypes)
        setQ(prev => ({
          items: offset ? prev.items.concat(res.data || []) : withPinned(res.data || []),
          count: res.count || 0,
          counts: res.counts || null,
          checkCounts: res.checkCounts || {},
          eligibleInView: res.eligibleInView || 0,
          valueOpen: typeof res.valueOpen === 'number' ? res.valueOpen : null,
          valueOpenExcludedLines: res.valueOpenExcludedLines || 0,
          settings: res.settings || null,
          dataAsOf: res.dataAsOf || null
        }))
        if (!offset) {
          setSel({})
          setCurId(prev => (pinned.current ? pinned.current.voucherId
            : (res.data || []).some(i => i.voucherId === prev) ? prev : ((res.data || [])[0] || {}).voucherId || null))
        }
        if (Array.isArray(res.runningBatches)) {
          setRunning(prev => {
            const ids = {}; prev.forEach(b => { ids[b.batchId] = 1 })
            return prev.concat(res.runningBatches.filter(b => !ids[b.batchId]).map(b => ({ batchId: b.batchId, voucherCount: b.voucherCount })))
          })
        }
      })
      .catch(err => { if (my === seq.current) setError(errorText(err)) })
      .then(() => { if (my === seq.current) { setLoading(false); setLoadingMore(false) } })
  }, [filterKey]) // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => { if (perms.r) load(0) }, [load, perms.r])

  useEffect(() => {
    const dl = deepLink.current
    if (!dl || !perms.r) return
    deepLink.current = null
    getVoucher(dl.type, dl.id)
      .then(res => {
        if (!res || !res.item) return
        pinned.current = res.item
        setQ(prev => ({ ...prev, items: withPinned(prev.items.filter(x => x.voucherId !== res.item.voucherId)) }))
        setCurId(res.item.voucherId)
      })
      .catch(err => message.error('Could not open the linked voucher: ' + errorText(err), 8))
  }, [perms.r]) // eslint-disable-line react-hooks/exhaustive-deps

  const items = q.items
  const cur = items.find(i => i.voucherId === curId) || null

  // ---- detail (history + changes), cached per voucher until it is acted on ----
  const [details, setDetails] = useState({})
  const [detailLoading, setDetailLoading] = useState(false)
  // Bumped after a voucher's cached detail is dropped. React 16 does not batch
  // setState calls made in promise callbacks, so the row and the cache update in
  // separate renders; this makes the refetch depend on the cache being gone.
  const [detailNonce, setDetailNonce] = useState(0)
  useEffect(() => {
    if (!cur) return
    if (details[cur.voucherId]) { setDetailLoading(false); return }
    let alive = true
    setDetailLoading(true)
    getVoucher(cur.voucherType, cur.voucherId)
      .then(res => { if (alive) setDetails(d => ({ ...d, [cur.voucherId]: { history: res.history || [], changes: res.changes || [] } })) })
      .catch(err => { if (alive) message.error('Could not load history for ' + cur.voucherNo + ': ' + errorText(err)) })
      .then(() => { if (alive) setDetailLoading(false) })
    return () => { alive = false }
  }, [curId, detailNonce]) // eslint-disable-line react-hooks/exhaustive-deps

  // ---- per-voucher working state ----
  // What the auditor flagged, per voucher: { voucherId: { flagKey: { lineId, field } } }. lineId null =
  // a voucher field. A line can carry several flags (one per field). Kept until the voucher is decided.
  const [flagMap, setFlagMap] = useState({})
  const [remark, setRemark] = useState('')
  const [busy, setBusy] = useState(null)
  const remarkRef = useRef(null)
  useEffect(() => { setRemark('') }, [curId])
  const flagsFor = (id) => flagMap[id] || {}
  const editFlags = (change) => {
    if (!cur) return
    const id = cur.voucherId
    setFlagMap(prev => {
      const mine = change({ ...(prev[id] || {}) })
      const next = { ...prev }
      if (Object.keys(mine).length) next[id] = mine; else delete next[id]
      return next
    })
  }
  // One field on or off (voucher field when lineId is null).
  const toggleFlag = (lineId, field) => editFlags(mine => {
    const k = flagKey(lineId, field)
    if (mine[k]) delete mine[k]; else mine[k] = { lineId: lineId || null, field }
    return mine
  })
  // Line's Flag button / number key: an unflagged line gets the field its checks point to;
  // a flagged line has all of its flags cleared.
  const toggleLine = (line) => editFlags(mine => {
    const own = Object.keys(mine).filter(k => mine[k].lineId === line.lineId)
    if (own.length) own.forEach(k => { delete mine[k] })
    else mine[flagKey(line.lineId, suggestField(line))] = { lineId: line.lineId, field: suggestField(line) }
    return mine
  })

  // Replace one row with the server's fresh copy; drop it if it no longer fits the view.
  const neighbourOf = (id) => {
    const i = items.findIndex(x => x.voucherId === id)
    const n = items[i + 1] || items[i - 1]
    return n ? n.voucherId : null
  }
  const applyRow = (fresh, opts) => {
    const keep = matchesView(fresh, status, check)
    const next = neighbourOf(fresh.voucherId)
    setQ(prev => ({
      ...prev,
      items: keep ? prev.items.map(x => (x.voucherId === fresh.voucherId ? fresh : x)) : prev.items.filter(x => x.voucherId !== fresh.voucherId),
      count: keep ? prev.count : Math.max(0, prev.count - 1)
    }))
    setDetails(d => { const n = { ...d }; delete n[fresh.voucherId]; return n })
    setDetailNonce(n => n + 1)
    setFlagMap(p => { const n = { ...p }; delete n[fresh.voucherId]; return n })
    setSel(s => { const n = { ...s }; delete n[fresh.voucherId]; return n })
    if (next && (!keep || (opts && opts.advance))) setCurId(next)
    setCountsStale(true)
  }
  // Tile and chip counts are server totals; after a single action they are a
  // step behind until the next queue load. Say so instead of guessing new numbers.
  const [countsStale, setCountsStale] = useState(false)
  useEffect(() => { setCountsStale(false) }, [q.counts])

  const onConflict = (err, item) => {
    if (errorCode(err) === 'VOUCHER_CHANGED') {
      message.warning(errorText(err), 6)
      getVoucher(item.voucherType, item.voucherId).then(res => res && res.item && applyRow(res.item)).catch(() => {})
      return true
    }
    return false
  }

  const doAudit = () => {
    if (!cur || !perms.c || busy) return
    if (statusOf(cur) === 'AUDITED') return
    const openQa = cur.audit && cur.audit.queryAlert && cur.audit.queryAlert.open ? cur.audit.queryAlert : null
    if (openQa) { message.warning('Audit Query ' + openQa.alertNo + ' is still open. Close it in the alert inbox first, then mark ' + cur.voucherNo + ' audited.', 6); return }
    if (Object.keys(flagsFor(cur.voucherId)).length) { message.info('Items are flagged on ' + cur.voucherNo + '. Raise a query instead, or clear the flags.'); return }
    if (allChecks(cur).length && !remark.trim()) {
      message.info('This voucher has open checks. Write why you accept it, then mark it audited.')
      if (remarkRef.current) remarkRef.current.focus()
      return
    }
    const item = cur
    setBusy('audit')
    auditVoucher({ voucherType: item.voucherType, voucherId: item.voucherId, expectedHash: item.hash, remarks: remark.trim() })
      .then(fresh => {
        message.success(item.voucherNo + ' audited.')
        applyRow(fresh, { advance: true }); setLogKey(k => k + 1)
      })
      .catch(err => { if (!onConflict(err, item)) message.error(errorText(err)) })
      .then(() => setBusy(null))
  }
  // Raise query opens the Audit Query window; submitting it raises the alert and the query together.
  const [queryOpen, setQueryOpen] = useState(false)
  const [users, setUsers] = useState([])
  const [usersState, setUsersState] = useState({ loaded: false, loading: false, error: null })
  const doQuery = () => {
    if (!cur || !perms.c || busy) return
    const qa = cur.audit && cur.audit.queryAlert
    if (qa && qa.open) { message.info('Audit Query ' + qa.alertNo + ' is still open for ' + cur.voucherNo + '. Follow it up in the alert inbox.'); return }
    setQueryOpen(true)
    if (!usersState.loaded && !usersState.loading) {
      setUsersState({ loaded: false, loading: true, error: null })
      getAssignableUsers()
        .then(list => { setUsers(list || []); setUsersState({ loaded: true, loading: false, error: null }) })
        .catch(err => setUsersState({ loaded: false, loading: false, error: 'Could not load users: ' + errorText(err) }))
    }
  }
  const submitQuery = ({ remarks, items, alert }) => {
    const item = cur
    setBusy('query')
    raiseQuery({ voucherType: item.voucherType, voucherId: item.voucherId, expectedHash: item.hash, remarks, items, alert })
      .then(fresh => {
        const qa = fresh.audit && fresh.audit.queryAlert
        message.success('Audit Query ' + (qa ? qa.alertNo + ' ' : '') + 'raised on ' + item.voucherNo + (qa && qa.assignee ? ' and assigned to ' + qa.assignee.name : '') + '.', 6)
        setQueryOpen(false); setRemark(''); applyRow(fresh); setLogKey(k => k + 1)
      })
      .catch(err => { if (!onConflict(err, item)) message.error(errorText(err), 8); else setQueryOpen(false) })
      .then(() => setBusy(null))
  }
  const doReopen = () => {
    if (!cur || !perms.reopen || busy || !remark.trim()) return
    const item = cur
    setBusy('reopen')
    reopenAudit({ voucherType: item.voucherType, voucherId: item.voucherId, remarks: remark.trim() })
      .then(fresh => { message.success(item.voucherNo + ' reopened.'); applyRow(fresh); setLogKey(k => k + 1) })
      .catch(err => message.error(errorText(err)))
      .then(() => setBusy(null))
  }

  // ---- bulk ----
  const [bulk, setBulk] = useState(null) // { mode: 'selected' | 'filter' }
  const [bulkBusy, setBulkBusy] = useState(false)
  const selItems = items.filter(i => sel[i.voucherId])
  const toggleSel = (it) => {
    if (!bulkEligible(it)) return
    setSel(s => {
      const n = { ...s }
      if (n[it.voucherId]) delete n[it.voucherId]
      else if (Object.keys(n).length >= BULK_MAX) { message.info('Select at most ' + BULK_MAX + ' vouchers, or use "Audit all clean" for more.'); return s } else n[it.voucherId] = true
      return n
    })
  }
  const selectAllClean = () => {
    const n = {}
    items.filter(bulkEligible).slice(0, BULK_MAX).forEach(i => { n[i.voucherId] = true })
    setSel(n)
  }
  const filterText = () => {
    const p = [type === 'ALL' ? 'PI and SPI' : type]
    p.push(range && range[0] ? range[0].format('D MMM YYYY') + ' to ' + range[1].format('D MMM YYYY') : 'All dates')
    if (location) { const l = locations.find(x => x._id === location); p.push(l ? l.name : 'Location filter') }
    if (search) p.push('Search "' + search + '"')
    return p.join(' · ')
  }
  const runBulk = (remarks) => {
    setBulkBusy(true)
    const requestId = newRequestId()
    if (bulk.mode === 'selected') {
      bulkAudit({ items: selItems.map(i => ({ voucherType: i.voucherType, voucherId: i.voucherId, expectedHash: i.hash })), remarks, filtersUsed: filters, requestId })
        .then(res => {
          const sk = (res.skipped || []).length
          message.success((res.audited || []).length + ' audited in batch ' + res.batchId + (sk ? '. ' + sk + ' left out because they changed or gained checks.' : '.'), 6)
          setBulk(null); setSel({}); setLogKey(k => k + 1); load(0)
        })
        .catch(err => message.error(errorText(err)))
        .then(() => setBulkBusy(false))
    } else {
      const f = { ...filters, status: 'NEW', check: 'NONE' }
      bulkAuditByFilter({ filters: f, expectedCount: q.eligibleInView, remarks, requestId })
        .then(res => {
          message.info('Batch ' + res.batchId + ' started for ' + res.voucherCount + ' vouchers. You can keep working.', 6)
          setRunning(r => r.concat([{ batchId: res.batchId, voucherCount: res.voucherCount }]))
          setBulk(null)
        })
        .catch(err => {
          message.error(errorText(err), 8)
          if (errorCode(err) === 'COUNT_CHANGED') { setBulk(null); load(0) }
        })
        .then(() => setBulkBusy(false))
    }
  }

  // Poll running batches until they finish; one getBatch per running batch every 5s.
  const runningKey = running.map(b => b.batchId).join(',')
  useEffect(() => {
    if (!running.length) return
    let stopped = false
    const tick = () => {
      Promise.all(running.map(b => getBatch(b.batchId).then(r => r, () => null))).then(res => {
        if (stopped) return
        const done = res.filter(r => r && (r.status === 'DONE' || r.status === 'FAILED'))
        if (!done.length) return
        done.forEach(r => {
          if (r.status === 'DONE') message.success('Batch ' + r.batchId + ' finished: ' + r.audited + ' audited' + ((r.skipped || []).length ? ', ' + r.skipped.length + ' left out' : '') + '.', 8)
          else message.error('Batch ' + r.batchId + ' failed: ' + (r.error || 'see the audit log') + '.', 10)
        })
        const ids = {}; done.forEach(r => { ids[r.batchId] = 1 })
        setRunning(rs => rs.filter(b => !ids[b.batchId]))
        setLogKey(k => k + 1)
        load(0)
      })
    }
    const t = setInterval(tick, BATCH_POLL_MS)
    return () => { stopped = true; clearInterval(t) }
  }, [runningKey]) // eslint-disable-line react-hooks/exhaustive-deps

  // ---- view, lightbox, export ----
  const [view, setView] = useState('queue')
  const [logKey, setLogKey] = useState(0)
  const [lb, setLb] = useState(null) // { images, index }
  const openDoc = (doc) => {
    if (!cur) return
    const imgs = (cur.documents || []).filter(isImage)
    if (!doc) { if (!imgs.length) return; doc = imgs[0] }
    if (!isImage(doc)) { window.open(doc.url, '_blank', 'noopener'); return }
    setLb({ images: imgs.map(d => ({ url: d.url, caption: cur.voucherNo + ' · ' + (d.name || 'Attachment'), sub: cur.vendor ? cur.vendor.name : '' })), index: imgs.indexOf(doc) })
  }
  const openPhoto = (line) => {
    const withPhoto = (cur.lines || []).filter(l => l.spare && l.spare.photo)
    setLb({ images: withPhoto.map(l => ({ url: l.spare.photo, caption: l.spare.name, sub: [l.spare.code, 'spare master photo'].filter(Boolean).join(' · ') })), index: withPhoto.indexOf(line) })
  }
  const [exporting, setExporting] = useState(false)
  const doExport = () => {
    setExporting(true)
    // The browser's timezone, so exported dates match what the manager sees on screen.
    let tz = null
    try { tz = Intl.DateTimeFormat().resolvedOptions().timeZone || null } catch (e) { tz = null }
    exportQueue({ ...filters, tz }).catch(err => message.error(errorText(err))).then(() => setExporting(false))
  }

  // ---- keyboard ----
  const nav = (d) => {
    const i = items.findIndex(x => x.voucherId === curId)
    const n = items[Math.min(items.length - 1, Math.max(0, i + d))]
    if (n) {
      setCurId(n.voucherId)
      const el = document.querySelector('.sax .qi[data-id="' + n.voucherId + '"]')
      if (el && el.scrollIntoView) el.scrollIntoView({ block: 'nearest' })
    }
  }
  const keyRef = useRef(null)
  keyRef.current = (e) => {
    if (view !== 'queue' || lb || bulk || queryOpen) return
    if (e.metaKey || e.ctrlKey || e.altKey) return
    const t = e.target || {}
    const tag = (t.tagName || '').toLowerCase()
    if (tag === 'input' || tag === 'textarea' || tag === 'select' || t.isContentEditable) return
    if (document.querySelector('.ant-select-dropdown:not(.ant-select-dropdown-hidden), .ant-calendar-picker-container')) return
    const k = e.key.toLowerCase()
    if (k === 'j' || e.key === 'ArrowDown') { e.preventDefault(); nav(1) } else if (k === 'k' || e.key === 'ArrowUp') { e.preventDefault(); nav(-1) } else if (k === 'a') { e.preventDefault(); doAudit() } else if (k === 'q') {
      e.preventDefault()
      doQuery()
    } else if (k === 'o') { e.preventDefault(); openDoc(null) } else if (k === 'x') { if (cur && perms.bulk) toggleSel(cur) } else if (/^[1-9]$/.test(e.key) && cur && perms.c && statusOf(cur) !== 'AUDITED') {
      const line = (cur.lines || [])[Number(e.key) - 1]
      if (line) toggleLine(line)
    }
  }
  useEffect(() => {
    const h = (e) => keyRef.current && keyRef.current(e)
    document.addEventListener('keydown', h)
    return () => document.removeEventListener('keydown', h)
  }, [])

  // ---- render ----
  if (!perms.r) {
    return (
      <div className='sax'>
        <div className='errbox'><b>You do not have access to Spares Audit.</b><span>Ask an administrator to give your role read permission on Spares Audit.</span></div>
      </div>
    )
  }

  const counts = q.counts
  const audited = counts ? counts.AUDITED : null
  const total = counts ? counts.total : null
  const pct = counts && total ? Math.round((audited / total) * 100) : 0
  const order = checkOrder()
  const chipCodes = order.concat(Object.keys(q.checkCounts || {}).filter(c => c !== 'NONE' && order.indexOf(c) < 0))
    .filter(c => q.checkCounts && q.checkCounts[c])

  return (
    <div className='sax'>
      {USE_MOCK && (
        <div className='mockbar'>
          <span className='tag'>Mock data</span>
          <span><b>Development mock.</b> Everything on this screen comes from <span className='mono'>SparesAudit/mockApi.js</span>, not the server.
            Turn it off by removing <span className='mono'>REACT_APP_SPARES_AUDIT_MOCK</span> from <span className='mono'>.env.local</span>.</span>
        </div>
      )}

      <div className='head'>
        <div>
          <h1>Spares Audit</h1>
          <p>Check spare purchases (PI) and spare issues (SPI): right spare, right unit, right quantity. New and edited vouchers come here until they are audited.</p>
        </div>
        <div className='views' role='group' aria-label='View'>
          <button type='button' aria-pressed={view === 'queue'} onClick={() => setView('queue')}>Audit queue</button>
          <button type='button' aria-pressed={view === 'log'} onClick={() => setView('log')}>Audit log</button>
        </div>
      </div>

      {running.length > 0 && (
        <div className='procbar'>
          <Spin size='small' />
          <span>Bulk batch{running.length > 1 ? 'es' : ''} running on the server: {running.map(b => b.batchId + ' (' + b.voucherCount + ' vouchers)').join(', ')}. The queue refreshes when {running.length > 1 ? 'they finish' : 'it finishes'}.</span>
        </div>
      )}

      {view === 'log' ? <AuditLog refreshKey={logKey} /> : (
        <section className='cockpit'>
          <div className='topbar'>
            <div className='fld'>
              <label>Voucher date</label>
              <DatePicker.RangePicker value={range} onChange={v => setRange(v || [])} format='D MMM YYYY' allowClear
                ranges={{ 'This month': [moment().startOf('month'), moment()], 'Last 30 days': [moment().subtract(29, 'days'), moment()] }} />
            </div>
            <div className='fld'>
              <label>Location</label>
              <Select style={{ width: 180 }} allowClear placeholder='All my locations' value={location} onChange={setLocation}
                showSearch optionFilterProp='children'>
                {locations.map(l => <Select.Option key={l._id} value={l._id}>{l.name}</Select.Option>)}
              </Select>
            </div>
            <div className='fld'>
              <label>Order</label>
              <Select style={{ width: 130 }} value={sort} onChange={setSort}>
                <Select.Option value='newest'>Newest first</Select.Option>
                <Select.Option value='oldest'>Oldest first</Select.Option>
              </Select>
            </div>
            <div className='fld grow'>
              <label>Search</label>
              <Input.Search allowClear value={searchInput} onChange={e => setSearchInput(e.target.value)} placeholder='Voucher, spare, part no., vendor, job card' />
            </div>
            <Button icon='download' onClick={doExport} loading={exporting} disabled={!!error}>Export</Button>
          </div>

          <div className='resultbar'>
            <div className='ring' aria-hidden='true'>
              <svg viewBox='0 0 36 36'>
                <circle cx='18' cy='18' r='15.5' fill='none' stroke='var(--surface-2)' strokeWidth='4' />
                <circle cx='18' cy='18' r='15.5' fill='none' stroke='var(--match)' strokeWidth='4' strokeLinecap='round' strokeDasharray={pct + ' 100'} pathLength='100' />
              </svg>
              <div className='pct'>{counts ? pct + '%' : '–'}</div>
            </div>
            <div className='cov'>
              <b>{counts ? audited + ' of ' + total + ' audited' : 'Loading…'}</b>
              <span>
                {q.valueOpen !== null ? inr(Math.round(q.valueOpen)) + ' still to vet' : 'Open value not available'}
                {q.valueOpenExcludedLines ? ' (' + q.valueOpenExcludedLines + ' lines without a value excluded)' : ''}
              </span>
              {countsStale && <span className='hint'>Counts update on the next refresh. <button type='button' className='lnk' onClick={() => load(0)}>Refresh</button></span>}
              {!countsStale && q.dataAsOf && <span className='hint'>Checks as of {moment(q.dataAsOf).format('HH:mm:ss')} <button type='button' className='lnk' onClick={() => load(0)}>Refresh</button></span>}
            </div>
            <div className='kpis'>
              {TILES.map(t => (
                <button type='button' key={t.key} className={'kpi ' + t.cls} aria-pressed={status === t.key}
                  onClick={() => { setStatus(t.key); setCheck(null) }}>
                  <span className='n'>{counts && counts[t.key] != null ? counts[t.key] : '–'}</span>
                  <span className='l'>{t.label}</span>
                </button>
              ))}
            </div>
          </div>

          <div className='qbar'>
            <div className='seg' role='group' aria-label='Voucher type'>
              {[['ALL', 'All'], ['PI', 'Purchases · PI'], ['SPI', 'Issues · SPI']].map(([k, l]) => (
                <button type='button' key={k} aria-pressed={type === k} onClick={() => setType(k)}>{l}</button>
              ))}
            </div>
            <div className='chips' aria-label='Filter by check'>
              {q.checkCounts && q.checkCounts.NONE > 0 && (
                <button type='button' className='chip ok' aria-pressed={check === 'NONE'} onClick={() => setCheck(check === 'NONE' ? null : 'NONE')}>
                  <span className='dot' />No checks <span className='b'>{q.checkCounts.NONE}</span>
                </button>
              )}
              {chipCodes.map(c => (
                <button type='button' key={c} className={'chip ' + (c === 'PENDING_INVOICE' ? 'info' : 'neutral')} aria-pressed={check === c} onClick={() => setCheck(check === c ? null : c)}>
                  <span className='dot' />{checkLabel(c)} <span className='b'>{q.checkCounts[c]}</span>
                </button>
              ))}
            </div>
            {perms.bulk && (
              <div className='bulkacts'>
                <button type='button' className='lnk' onClick={selectAllClean} disabled={!items.some(bulkEligible)}>Select clean (loaded)</button>
                <button type='button' className='lnk' onClick={() => setSel({})} disabled={!selItems.length}>Clear</button>
                {q.eligibleInView > 0 && (
                  <Button size='small' onClick={() => setBulk({ mode: 'filter' })}>Audit all {q.eligibleInView} clean…</Button>
                )}
              </div>
            )}
          </div>

          {q.settings && (q.settings.notConfigured || []).indexOf('FRACTIONAL_COUNT') >= 0 && (
            <div className='hint' style={{ padding: '6px 14px', borderBottom: '1px solid var(--border)' }}>
              Fractional-count check is off: no count units (such as nos, pcs, set) are configured for this organisation.
            </div>
          )}
          {error
            ? <div className='errbox' style={{ margin: 14 }}><b>Could not load the audit queue.</b><span>{error}</span><span><Button size='small' onClick={() => load(0)}>Try again</Button></span></div>
            : (
              <Spin spinning={loading}>
                <div className='workspace'>
                  <QueueList items={items} count={q.count} curId={curId} sel={sel}
                    canSelect={it => perms.bulk && bulkEligible(it)}
                    onPick={id => { if (pinned.current && pinned.current.voucherId !== id) pinned.current = null; setCurId(id) }} onToggleSel={toggleSel}
                    hasMore={items.length < q.count} loadingMore={loadingMore} onLoadMore={() => load(items.length)}
                    sortLabel={sort === 'oldest' ? 'Oldest first' : 'Newest first'} />
                  <FocusPanel links={links} item={cur} detail={cur ? details[cur.voucherId] : null} detailLoading={detailLoading}
                    flags={cur ? flagsFor(cur.voucherId) : {}} remark={remark} setRemark={setRemark} remarkRef={remarkRef}
                    busy={busy} perms={perms} onToggleLine={toggleLine} onToggleFlag={toggleFlag}
                    onAudit={doAudit} onQuery={doQuery} onReopen={doReopen} onNav={nav} onOpenDoc={openDoc} onPhoto={openPhoto} />
                </div>
              </Spin>)}
          <div className='help'>
            <span><kbd>J</kbd> <kbd>K</kbd> next / previous</span><span><kbd>A</kbd> mark audited</span>
            <span><kbd>Q</kbd> raise Audit Query</span><span><kbd>X</kbd> select for bulk</span>
            <span><kbd>O</kbd> open invoice</span><span><kbd>1</kbd>–<kbd>9</kbd> flag line</span>
            <span><kbd>Enter</kbd> in remark: mark audited</span>
          </div>
        </section>
      )}

      {view === 'queue' && selItems.length > 0 && (
        <div className='sax-dock'>
          <span><b>{selItems.length} selected</b> <small>· new, no checks</small></span>
          <button type='button' className='lnkw' onClick={() => setSel({})}>Clear</button>
          <Button type='primary' onClick={() => setBulk({ mode: 'selected' })}>Bulk audit {selItems.length}</Button>
        </div>
      )}

      <BulkDialog visible={!!bulk} mode={bulk ? bulk.mode : 'selected'} items={selItems}
        filterCount={q.eligibleInView} filterText={filterText()} busy={bulkBusy}
        onCancel={() => setBulk(null)} onConfirm={runBulk} />

      <RaiseQueryDialog visible={queryOpen} item={cur} flags={cur ? flagsFor(cur.voucherId) : {}} remark={remark}
        users={users} usersLoading={usersState.loading} usersError={usersState.error} busy={busy === 'query'}
        onCancel={() => setQueryOpen(false)} onSubmit={submitQuery} />

      <PhotoLightbox visible={!!lb} images={lb ? lb.images : []} index={lb ? lb.index : 0}
        onIndex={i => setLb(l => ({ ...l, index: i }))} onClose={() => setLb(null)} />
    </div>
  )
}

const mapStateToProps = ({ auth }) => ({ authUser: auth.authUser })

export default connect(mapStateToProps)(SparesAudit)
